<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BillingDateRuleFields',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    modelValue: {
      type: Object,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const offsetDays = ref(null);

  const $refs = {
    get offsetDays() {
      return offsetDays.value;
    },
  };

  const addRule = (...args) => {
    const [id] = args;
    const selectedRule = _.find(rules.value, { id });
    if (selectedRule) {
      _.extend(item.value, selectedRule.rule);
      setData('selectedRuleID', null);
    }
  };
  const onCheckChanged = (...args) => {
    if (isOffsetDaysSet.value) {
      setData('item.offset_days', 0);
    } else {
      setData('item.offset_days', null);
    }
  };

  const item = ref(null);
  const labelAlign = ref('right');
  const rule_type = ref('billing_date');
  const rules = ref(null);
  const selectedRuleID = ref(null);
  const isOffsetDaysSet = ref(null);
  const metadata = ref(null);
  const anchorOptions = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    item: item.value,
    labelAlign: labelAlign.value,
    rule_type: rule_type.value,
    rules: rules.value,
    selectedRuleID: selectedRuleID.value,
    isOffsetDaysSet: isOffsetDaysSet.value,
    metadata: metadata.value,
    anchorOptions: anchorOptions.value,
    ux: ux.value,
  }));

  const $$load_item = () => {
    return props.modelValue;
  };
  const $$load_rules = () => {
    return $app.value.repo
      .get('list_object_by_type', {
        parameters: { object_type: 'billing-rule' },
      })
      .then((items) =>
        _.chain(items).filter({ rule_type: rule_type.value }).value()
      );
  };
  const $$load_isOffsetDaysSet = () => {
    return item.value.offset_days != null;
  };
  const $$load_metadata = () => {
    return $app.value.repo.get('ui_metadata');
  };
  const $$load_anchorOptions = () => {
    return metadata.value.billing_date_rule_anchor_options;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'labelAlign' },
      { name: 'rule_type' },
      { name: 'rules' },
      { name: 'selectedRuleID' },
      { name: 'isOffsetDaysSet', dependsOn: ['item'] },
      { name: 'metadata' },
      { name: 'anchorOptions', dependsOn: ['metadata'] },
      { name: 'ux' },
    ],
    loaders: {
      item: $$load_item,
      rules: $$load_rules,
      isOffsetDaysSet: $$load_isOffsetDaysSet,
      metadata: $$load_metadata,
      anchorOptions: $$load_anchorOptions,
    },
    ux: {},
    dataRefs: {
      item,
      labelAlign,
      rule_type,
      rules,
      selectedRuleID,
      isOffsetDaysSet,
      metadata,
      anchorOptions,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.modelValue,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modelValue');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_6705964765214521_script_a93372k75_value_callable = async (
    $event
  ) => {
    return addRule(selectedRuleID.value);
  };
  const act_6705964765214521_script_a93372k75_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6705964765214521_script_a93372k75_value_callable($event);
      },
    };
  };
  const act_4063326939524293_script_ymsb1uuf9_value_callable = async (
    $event
  ) => {
    return nextTick(() => onCheckChanged(isOffsetDaysSet.value));
  };
  const act_4063326939524293_script_ymsb1uuf9_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4063326939524293_script_ymsb1uuf9_value_callable($event);
      },
    };
  };

  const act_6705964765214521_script_a93372k75 = new actionTypes.ScriptAction({
    actionArgs: act_6705964765214521_script_a93372k75_getActionArgs,
  });
  const inst_evh_6705964765214521_click_4qs3odcou =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_script_a93372k75,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_6705964765214521_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6705964765214521_click_4qs3odcou],
    event: 'click',
  });
  const act_4063326939524293_script_ymsb1uuf9 = new actionTypes.ScriptAction({
    actionArgs: act_4063326939524293_script_ymsb1uuf9_getActionArgs,
  });
  const inst_evh_4063326939524293_change_b84mr8d6g =
    new actionTypes.EventHandlerImpl({
      action: act_4063326939524293_script_ymsb1uuf9,
      event: 'change',
      displayName: 'script',
    });
  const inst_evh_4063326939524293_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4063326939524293_change_b84mr8d6g],
    event: 'change',
  });

  const actions = {
    inst_evh_6705964765214521_click_4qs3odcou,
    inst_evh_6705964765214521_click,
    inst_evh_4063326939524293_change_b84mr8d6g,
    inst_evh_4063326939524293_change,
  };

  const evh_6705964765214521_click_4qs3odcou = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click_4qs3odcou.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4063326939524293_change_b84mr8d6g = async (event, scope) => {
    return await actions.inst_evh_4063326939524293_change_b84mr8d6g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4063326939524293_change = async (event, scope) => {
    return await actions.inst_evh_4063326939524293_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div class="row mb-3" v-if="loaded">
    <div class="col" v-if="!readonly">
      <div class="input-group">
        <span class="input-group-text">
          <ItpIcon icon="material-symbols:calendar-month"> </ItpIcon>
        </span>
        <ItpFormNativeSelect
          name="selectedRuleID"
          v-model="selectedRuleID"
          :options="rules"
          valueField="id"
          textField="rule.description"
          :placeholder="
            pgettext('BillingDateRule', 'Select predefined rule to add.')
          "
        >
        </ItpFormNativeSelect>
        <ItpButton
          variant="light"
          icon="fa-plus"
          :tooltip="pgettext('BillingDateRule', 'Add rule configuration')"
          :disabled="!selectedRuleID"
          @click="evh_6705964765214521_click($event, {})"
        >
        </ItpButton>
      </div>
    </div>
  </div>
  <div class="row mb-3" v-if="loaded">
    <div class="form-group col-md-8">
      <ItpFormNativeSelect
        name="billing_date_rule_anchor"
        v-model="item.anchor"
        :options="anchorOptions"
        valueField="value"
        textField="name"
        required
        :readonly="readonly"
      >
      </ItpFormNativeSelect>
    </div>
    <div class="form-group col-md-4">
      <div class="input-group">
        <div class="input-group-text">
          <input
            type="checkbox"
            v-model="isOffsetDaysSet"
            :readonly="readonly"
            @change="evh_4063326939524293_change($event, {})"
          />
        </div>
        <ItpFormInput
          type="number"
          name="offset_days"
          v-model="item.offset_days"
          :disabled="!isOffsetDaysSet"
          :readonly="readonly"
          ref="offsetDays"
        >
        </ItpFormInput>
        <span class="input-group-text">
          {{ pgettext('InputGroupAddon', 'day') }}
        </span>
      </div>
    </div>
  </div>
  <div class="row" v-if="loaded">
    <div class="col">
      <ItpFormInput
        name="description"
        v-model="item.description"
        :placeholder="pgettext('BillingDateRule', 'Rule description')"
        :readonly="readonly"
        v-if="loaded"
      >
      </ItpFormInput>
    </div>
  </div>
</template>
